import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useIntlInstance } from "core/hooks/useIntlInstance";
import { isSameDay } from "date-fns/isSameDay";
import { useTranslation } from "react-i18next";

type BigEventWidgetTileProps = {
  id: string;
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
  title: string;
  address?: string | null | undefined;
  url?: string | null | undefined;
};

export function BigEventWidgetTile({
  id,
  startDate,
  endDate,
  title,
  address,
  url,
}: BigEventWidgetTileProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventsWidget",
  });
  const getMonth = useFormatDate({ month: "short" });
  const formatDate = useFormatDate({
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const formatTime = useFormatDate({ timeStyle: "short" });
  const intlInstance = useIntlInstance();
  const order: ["month", "day"] | ["day", "month"] = intlInstance
    .formatToParts(new Date())
    .map((part) => part.type)
    .filter((type) => ["month", "day"].includes(type)) as any;

  if (!startDate || !endDate) {
    return null;
  }
  const isOneDayEvent = isSameDay(new Date(startDate), new Date(endDate));
  const startDateMonth = getMonth(startDate);
  const dateOfMonth = new Date(startDate).getDate();
  const formattedStartTime = formatTime(startDate);
  const formattedEndDate = formatDate(endDate);
  const eventHref = `/events/${id}`;

  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: {
          sm: (theme) => `1px solid ${theme.palette.divider}`,
          md: "none",
        },
      }}
    >
      <CustomLink href={eventHref}>
        <Stack
          width="min(300px, 100%)"
          mx="auto"
          borderRadius="5px"
          bgcolor="primary.main"
          color="primary.contrastText"
          textAlign="center"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ containerType: "inline-size", aspectRatio: "1 / 1" }}
          py={2}
          mb={1}
        >
          {order.map((part) => {
            if (part === "month") {
              return (
                <Typography key="month" variant="h3" lineHeight={1}>
                  {startDateMonth}
                </Typography>
              );
            } else {
              return (
                <Typography key="day" variant="h3" lineHeight={1}>
                  {dateOfMonth}
                </Typography>
              );
            }
          })}
          <Typography variant="h6" gutterBottom lineHeight={1}>
            {formattedStartTime}
          </Typography>
          {!isOneDayEvent && (
            <Typography fontSize="h6" lineHeight={1}>
              {t("toDate", { date: formattedEndDate })}
            </Typography>
          )}
        </Stack>
      </CustomLink>
      <Stack mt={2} spacing={1} alignItems="center">
        <CustomLink href={eventHref}>
          <Typography variant="h5">{title}</Typography>
        </CustomLink>
        {address && (
          <CustomLink
            target="_blank"
            href={`https://www.google.com/maps/search/${encodeURIComponent(
              address
            )}`}
          >
            <Typography color="text.secondary">
              <LocationOnIcon sx={{ marginBottom: "-3px" }} color="primary" />{" "}
              {address}
            </Typography>
          </CustomLink>
        )}
        {url && (
          <CustomLink
            href={url}
            target="_blank"
            maxWidth="100%"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            <OpenInNew sx={{ marginBottom: "-3px" }} color="primary" /> {url}
          </CustomLink>
        )}
        <Button href={eventHref} LinkComponent={CustomLink}>
          {t("details")}
        </Button>
      </Stack>
    </Box>
  );
}
